import { useEffect, useMemo, useRef, useState } from 'react';

const EMPTY_FORM = {
  idEmpresa: 0,
  nombre: '',
  idPadre: '',
  activo: true,
};

export default function EmpresasModal({
  open,
  mode = 'new',
  data = null,
  onClose,
  onSubmit,
  loading = false,
  inline = false,
  parentOptions = [],
}) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({ nombre: '' });
  const nameInputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    if (mode === 'edit' && data) {
      setForm({
        idEmpresa: data.idEmpresa ?? 0,
        nombre: data.nombre ?? '',
        idPadre: data.idPadre != null ? String(data.idPadre) : '',
        activo: data.activo ?? true,
      });
      validateField('nombre', data.nombre ?? '');
    } else {
      setForm({ ...EMPTY_FORM });
      setErrors({ nombre: '' });
    }
  }, [open, mode, data]);

  useEffect(() => {
    if (!open) return;
    const input = nameInputRef.current;
    if (input) {
      const pos = input.value.length;
      input.focus();
      // Solo ajustar selección al abrir o cambiar de registro
      input.setSelectionRange(pos, pos);
    }
  }, [open, mode, data]);

  const validateField = (field, rawValue) => {
    const value = typeof rawValue === 'string' ? rawValue.trim() : rawValue;
    let error = '';

    if (field === 'nombre' && !value) {
      error = 'Captura el nombre de la empresa.';
    }

    setErrors((prev) => ({ ...prev, [field]: error }));
    return !error;
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (field === 'nombre') validateField('nombre', value);
  };

  const handleSubmit = () => {
    if (!validateField('nombre', form.nombre)) return;
    const payload = {
      ...form,
      idPadre: form.idPadre === '' ? null : Number(form.idPadre),
    };
    onSubmit?.(payload, mode);
  };

  const availableParents = useMemo(() => {
    return parentOptions
      .filter((opt) => opt.idEmpresa !== form.idEmpresa)
      .map((opt) => ({
        value: String(opt.idEmpresa),
        label: opt.nombre || `Empresa #${opt.idEmpresa}`,
      }));
  }, [parentOptions, form.idEmpresa]);

  if (!open) return null;

  const ModalBody = (
    <div
      style={{
        paddingLeft: '15%',
        paddingRight: 16,
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '0.8rem',
        overflowY: 'auto',
        flex: '1 1 auto',
      }}
    >
      <div>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>Nombre *</label>
        <br />
        <br />
        <input
          className={`infoField${errors.nombre ? ' infoField--error' : ''}`}
          ref={nameInputRef}
          value={form.nombre}
          onChange={(e) => handleChange('nombre', e.target.value)}
          maxLength={120}
        />
        {errors.nombre && (
          <small style={{ color: '#b91c1c', fontSize: '.8rem' }}>{errors.nombre}</small>
        )}
      </div>

      <div>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>Empresa padre</label>
        <br />
        <br />
        <select
          className="infoField"
          value={form.idPadre ?? ''}
          onChange={(e) => handleChange('idPadre', e.target.value)}
        >
          <option value="">Sin empresa padre</option>
          {availableParents.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>
    </div>
  );

  const ModalFooter = (
    <div
      style={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '.6rem',
        marginTop: '.8rem',
        paddingRight: 16,
      }}
    >
      <button className="roles-btn" onClick={onClose}>Cancelar</button>
      <button
        className="btn-action"
        onClick={handleSubmit}
        disabled={!form.nombre.trim() || loading}
        style={{
          opacity: (form.nombre.trim() && !loading) ? 1 : 0.5,
          cursor: (form.nombre.trim() && !loading) ? 'pointer' : 'not-allowed',
        }}
      >
        {loading ? (mode === 'new' ? 'Guardando...' : 'Actualizando...') : (mode === 'new' ? 'Guardar' : 'Actualizar')}
      </button>
    </div>
  );

  const Content = (
    <>
      <div className="roles-topbar" style={{ paddingRight: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h3 className="roles-title" style={{ marginRight: 0 }}>
          {mode === 'new' ? 'Nueva empresa' : 'Editar empresa'}
        </h3>
        <button className="roles-btn" aria-label="Cerrar" onClick={onClose}>
          ✕
        </button>
      </div>
      {ModalBody}
      {ModalFooter}
    </>
  );

  if (inline) {
    return (
      <div
        className="roles-inline-card"
        style={{ display: 'flex', flexDirection: 'column', gap: 0 }}
      >
        {Content}
      </div>
    );
  }

  return (
    <div
      className="roles-confirm-overlay"
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,23,42,.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        zIndex: 1000,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        className="roles-confirm-card"
        style={{
          width: 'min(640px, 96vw)',
          maxHeight: '85vh',
          background: '#fff',
          borderRadius: 12,
          boxShadow: '0 10px 30px rgba(0,0,0,.25)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {Content}
      </div>
    </div>
  );
}
